import { AnimatePresence, motion } from "framer-motion"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import useAppStore from "../../../store/useAppStore"
import {
    calculateEffectiveWorkedMinutes,
    getLogsForCalendarDay,
    sortEmployeeDayLogs
} from "../../../utils/employeeDayEffectiveMinutes"
import { formatMonthWorkHoursMinutes } from "../../../utils/employeeMonthWorkSummary"
import {
    formatAttendanceTypeCell,
    formatLogConfirmerCell,
    formatLogEditorCell,
    formatLogLocationCell,
    LogConfirmationStatusIcon
} from "./employeeDayLogTableCells"

const formatSelectedDateLabel = (day) =>
    `${String(day.date).padStart(2, "0")}.${String(day.month + 1).padStart(2, "0")}.${day.year}`

const getLogRowKey = (logsTableKey, log, idx) =>
    log._id ? `${logsTableKey}-${log._id}` : `${logsTableKey}-${idx}-${log.vreme}-${log.status}`

const LOG_STATUS_ARROW = {
    ulaz: { color: "#16a34a", labelKey: "dayLogs.statusEntry", path: "M5 12h12M13 8l4 4-4 4" },
    izlaz: { color: "#dc2626", labelKey: "dayLogs.statusExit", path: "M19 12H7M11 8l-4 4 4 4" }
}

const LogStatusIcon = ({ status }) => {
    const { t } = useTranslation("stats")
    const cfg = LOG_STATUS_ARROW[status]
    if (!cfg) {
        const text = status && String(status).trim() ? status : "—"
        return <span>{text}</span>
    }
    const label = t(cfg.labelKey)
    return (
        <span className="asv-emp-day-log-status-icon" title={label} aria-label={label}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
                <path
                    d={cfg.path}
                    fill="none"
                    stroke={cfg.color}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </span>
    )
}

const UserStatsDayLogsTable = ({ isDarkTheme, selectedDay }) => {
    const { t } = useTranslation("stats")
    const logsByDay = useAppStore((state) => state.userStatsMonthLogsByDay)
    const loading = useAppStore((state) => state.loadingUserStatsMonthLogs)
    const employeesData = useAppStore((state) => state.employeesData)

    const selectedLogs = useMemo(() => {
        if (!selectedDay) return []
        if (logsByDay == null) return []
        const raw = getLogsForCalendarDay(logsByDay, selectedDay.year, selectedDay.month, selectedDay.date)
        return sortEmployeeDayLogs(raw)
    }, [selectedDay, logsByDay])

    const totalInsideFormatted = useMemo(() => {
        if (!selectedLogs.length) return null
        const minutes = calculateEffectiveWorkedMinutes(selectedLogs)
        return formatMonthWorkHoursMinutes(minutes ?? 0)
    }, [selectedLogs])

    const logsTableKey = selectedDay
        ? `${selectedDay.year}-${selectedDay.month}-${selectedDay.date}`
        : "none"

    const logsSectionDark = isDarkTheme ? " asv-emp-day-logs-section--dark" : ""
    const showTotal = totalInsideFormatted != null

    return (
        <div className={`usv-panel usv-panel--table asv-emp-day-logs-section${logsSectionDark}`}>
            <div className="asv-emp-day-logs-table-host">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={logsTableKey}
                        className="asv-emp-day-logs-table-animated"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.16, ease: "easeOut" }}
                    >
                        <div className="asv-emp-day-logs-table-scroll">
                            {!selectedDay ? (
                                <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                    {t("dayLogs.clickDayHint")}
                                </p>
                            ) : loading ? (
                                <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                    {t("dayLogs.loading")}
                                </p>
                            ) : logsByDay == null ? (
                                <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                    {t("dayLogs.notLoaded")}
                                </p>
                            ) : selectedLogs.length === 0 ? (
                                <p className={`asv-emp-day-logs-placeholder ${isDarkTheme ? "dark-color" : ""}`}>
                                    {t("dayLogs.emptyDay")}
                                </p>
                            ) : (
                                <table className={`asv-emp-day-logs-table ${isDarkTheme ? "asv-emp-day-logs-table--dark" : ""}`}>
                                    <thead>
                                        <tr>
                                            <th>{t("dayLogs.columns.time")}</th>
                                            <th className="asv-emp-day-logs-table-col-status" aria-label={t("dayLogs.columns.status")}>
                                                {t("dayLogs.columns.status")}
                                            </th>
                                            <th>{t("dayLogs.columns.attendanceType")}</th>
                                            <th>{t("dayLogs.columns.userConfirmation")}</th>
                                            <th className="asv-emp-day-logs-table-col-confirm" aria-label={t("dayLogs.confirmationStatusAria")} />
                                            <th>{t("dayLogs.columns.location")}</th>
                                            <th>{t("dayLogs.columns.editedBy")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedLogs.map((log, idx) => {
                                            const rowKey = getLogRowKey(logsTableKey, log, idx)
                                            return (
                                                <tr key={rowKey} className="asv-emp-day-log-row">
                                                    <td>{log.vreme ?? "—"}</td>
                                                    <td className="asv-emp-day-logs-table-col-status">
                                                        <LogStatusIcon status={log.status} />
                                                    </td>
                                                    <td>{formatAttendanceTypeCell(log, t)}</td>
                                                    <td>{formatLogConfirmerCell(log, employeesData)}</td>
                                                    <td className="asv-emp-day-logs-table-col-confirm">
                                                        <LogConfirmationStatusIcon log={log} />
                                                    </td>
                                                    <td className="asv-emp-day-logs-table-cell-loc">{formatLogLocationCell(log)}</td>
                                                    <td>{formatLogEditorCell(log, employeesData)}</td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        {showTotal ? (
                            <div className="asv-emp-day-logs-table-total" role="status">
                                <span className="asv-emp-day-logs-table-total-label">{t("dayLogs.total")}</span>
                                <span className="asv-emp-day-logs-table-total-value">{totalInsideFormatted}</span>
                            </div>
                        ) : null}
                    </motion.div>
                </AnimatePresence>
            </div>
            <div className={`asv-emp-day-logs-section-title ${isDarkTheme ? "dark-color" : ""}`}>
                {selectedDay
                    ? t("dayLogs.logsForDate", { date: formatSelectedDateLabel(selectedDay) })
                    : t("dayLogs.selectDay")}
            </div>
        </div>
    )
}

export default UserStatsDayLogsTable
